<template>
  <div class="media-page page-ind-canales h-full w-full p-4">

    <!-- <section class="media-hero mb-5">
      <h2 class="text-xl font-bold mb-2">
        Metas por canal
        <span v-if="sectionLoading" class="kpi-loading-dot" title="Cargando..."></span>
      </h2>
      <p class="hero-subtitle">Meta de spots, IA y fecha de inicio por canal.</p>
    </section> -->

    <section class="ind-toolbar mb-4">
      <div class="ind-toolbar-search">
        <i class="pi pi-search ind-search-icon"></i>
        <InputText
          v-model="filtro"
          placeholder="Buscar canal o cliente..."
          class="ind-filter-input ind-filter-input-with-icon"
        />
      </div>
      <div class="ind-toolbar-types">
        <div class="ind-type-filters" role="group" aria-label="Filtrar por tipo de canal">
          <label
            v-for="opt in tipoFiltroOptions"
            :key="opt.value"
            class="ind-type-chip"
            :class="{ 'is-active': tipoFiltro === opt.value }"
          >
            <input
              type="radio"
              :checked="tipoFiltro === opt.value"
              @change="tipoFiltro = opt.value"
            />
            <span>{{ opt.label }}</span>
          </label>
        </div>
      </div>
    </section>

    <div class="ind-toolbar-count">
      <span class="text-xs text-zinc-400 dark:text-zinc-500">{{ radiosFiltrados.length }} canales del filtro activo</span>
    </div>

    <!-- Skeleton inicial (carga en frío) -->
    <template v-if="loading && !radios.length">
      <div class="kpi-skel-page">
        <section class="kpi-section mb-5">
          <h3 class="kpi-section-title">
            <i class="pi pi-megaphone" style="font-size:0.92rem;color:#3b82f6;opacity:0.85"></i>
            Canales
          </h3>
          <KpiSectionSkeleton variant="table" :item-count="6" :col-count="6" />
        </section>
        <div class="text-center mt-2 mb-4">
          <span class="kpi-skel-loading-label">
            <i class="pi pi-spin pi-spinner mr-1.5" style="font-size:0.7rem"></i>
            Cargando canales…
          </span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="kpi-page-content" :class="{ 'canales-container--loading': sectionLoading }">
        <section class="media-table-wrap" :class="{ 'kpi-section--loading': sectionLoading }">
        <DataTable
          :value="radiosFiltrados"
          responsiveLayout="scroll"
          stripedRows
          class="text-sm"
        >
          <Column field="name" header="Canal / Cliente" sortable style="min-width:240px">
            <template #body="{ data }">
              <div class="flex items-center gap-2.5">
                <div
                  class="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
                  :style="logoBackgroundStyle(data)"
                >
                  <img
                    v-if="getLogoUrl(data)"
                    :src="getLogoUrl(data)"
                    :alt="data.name || ''"
                    class="max-w-full max-h-full object-contain"
                    draggable="false"
                  />
                  <i v-else class="pi pi-megaphone text-white" style="font-size:0.85rem;opacity:0.5"></i>
                </div>
                <div class="flex flex-col leading-tight gap-1 min-w-0">
                  <span class="font-semibold text-zinc-700 dark:text-zinc-200 truncate">{{ data.name }}</span>
                  <span class="text-xs text-zinc-400 dark:text-zinc-500 truncate">{{ data.client_name }}</span>
                </div>
              </div>
            </template>
          </Column>
          <Column field="type" header="Tipo" sortable style="width:90px" />
          <Column field="obligation_spots" header="Meta Spots" sortable style="width:110px">
            <template #body="{ data }">
              <span :class="data.obligation_spots == null ? 'text-zinc-400 italic' : ''">
                {{ data.obligation_spots != null ? data.obligation_spots : '—' }}
              </span>
            </template>
          </Column>
          <Column field="obligation_ia" header="Meta IA" sortable style="width:90px">
            <template #body="{ data }">{{ data.obligation_ia ?? 0 }}</template>
          </Column>
          <Column field="inicio_operaciones_at" header="Inicio Operaciones" sortable style="width:160px">
            <template #body="{ data }">
              <span :class="!data.inicio_operaciones_at ? 'text-zinc-400 italic' : ''">
                {{ data.inicio_operaciones_at || '—' }}
              </span>
            </template>
          </Column>
          <Column header="" style="width:60px">
            <template #body="{ data }">
              <Button
                v-if="isAdmin"
                icon="pi pi-pencil"
                class="p-button-text p-button-sm p-button-secondary"
                @click="abrirEditor(data)"
              />
            </template>
          </Column>
        </DataTable>
        </section>
      </div>
    </template>

    <!-- Dialog de edición -->
    <Dialog
      v-model:visible="dialogVisible"
      :header="canalActivo ? canalActivo.name : ''"
      :modal="true"
      :style="{ width: '480px' }"
      :closable="!guardando"
    >
      <div v-if="canalActivo" class="flex flex-col gap-5 pt-1">

        <!-- ── Cabecera: Cliente ── -->
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center" :style="logoBackgroundStyle(canalActivo)">
            <img v-if="getLogoUrl(canalActivo)" :src="getLogoUrl(canalActivo)" class="max-w-full max-h-full object-contain" draggable="false" />
            <i v-else class="pi pi-megaphone text-white" style="font-size:0.8rem;opacity:0.5"></i>
          </div>
          <div>
            <div class="text-xs text-zinc-400 uppercase tracking-wide font-semibold">Cliente</div>
            <div class="text-sm font-medium text-zinc-700 dark:text-zinc-200">{{ canalActivo.client_name }}</div>
          </div>
        </div>

        <!-- ── SECCIÓN: Información general ── -->
        <div class="kpi-canal-section">
          <div class="kpi-canal-section-label">
            <i class="pi pi-tag"></i>
            Información general
          </div>

          <div class="flex flex-col gap-1">
            <label class="kpi-canal-field-label">Tipo de canal</label>
            <select v-model="form.type" class="kpi-canal-select">
              <option value="">Sin tipo</option>
              <option v-for="tipo in typeOptions" :key="tipo" :value="tipo">{{ tipo }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1">
            <label class="kpi-canal-field-label">Fecha inicio de operaciones</label>
            <Calendar
              v-model="form.inicio_operaciones_at"
              dateFormat="yy-mm-dd"
              :showButtonBar="true"
              placeholder="Sin fecha definida"
              class="w-full"
            />
          </div>
        </div>

        <!-- ── SECCIÓN: Metas ── -->
        <div class="kpi-canal-section">
          <div class="kpi-canal-section-label">
            <i class="pi pi-chart-line"></i>
            Metas de producción
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="kpi-canal-field-label">Spots mensuales</label>
              <InputNumber v-model="form.obligation_spots" :min="0" :useGrouping="false" placeholder="Meta del cliente" class="w-full" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="kpi-canal-field-label">Spots IA mensuales</label>
              <InputNumber v-model="form.obligation_ia" :min="0" :useGrouping="false" placeholder="0" class="w-full" />
            </div>
          </div>
        </div>

        <!-- ── SECCIÓN: Configuración avanzada ── -->
        <div class="kpi-canal-section">
          <div class="kpi-canal-section-label">
            <i class="pi pi-sliders-h"></i>
            Configuración avanzada
          </div>

          <div class="kpi-canal-toggle-row" @click="form.requiere_llamada = !form.requiere_llamada">
            <div class="kpi-canal-toggle-info">
              <span class="kpi-canal-toggle-title">
                <i class="pi pi-phone" style="font-size:0.75rem;opacity:0.5"></i>
                Requiere llamada / minuta
              </span>
              <span class="kpi-canal-toggle-desc">Registro de contacto mensual obligatorio</span>
            </div>
            <InputSwitch :modelValue="form.requiere_llamada" @update:modelValue="form.requiere_llamada = $event" />
          </div>

          <div class="kpi-canal-toggle-row" @click="form.meta_cuenta = !form.meta_cuenta">
            <div class="kpi-canal-toggle-info">
              <span class="kpi-canal-toggle-title">
                <i class="pi pi-globe" style="font-size:0.75rem;opacity:0.5"></i>
                Cuenta en el global
              </span>
              <span class="kpi-canal-toggle-desc">Incluir en el cómputo general del KPI</span>
            </div>
            <InputSwitch :modelValue="form.meta_cuenta" @update:modelValue="form.meta_cuenta = $event" />
          </div>

          <div class="kpi-canal-toggle-row" @click="form.produccion_100_meta = !form.produccion_100_meta">
            <div class="kpi-canal-toggle-info">
              <span class="kpi-canal-toggle-title">
                <i class="pi pi-check-circle" style="font-size:0.75rem;opacity:0.5"></i>
                Producción siempre al 100%
              </span>
              <span class="kpi-canal-toggle-desc">Lo producido cuenta como meta cumplida, no penaliza el KPI global</span>
            </div>
            <InputSwitch :modelValue="form.produccion_100_meta" @update:modelValue="form.produccion_100_meta = $event" />
          </div>

          <div v-if="canalActivo && canalActivo.type === 'Sin canal'" class="kpi-canal-toggle-row" @click="form.incluir_en_kpi = !form.incluir_en_kpi">
            <div class="kpi-canal-toggle-info">
              <span class="kpi-canal-toggle-title">
                <i class="pi pi-chart-line" style="font-size:0.75rem;opacity:0.5"></i>
                Incluir en KPI
              </span>
              <span class="kpi-canal-toggle-desc">Aparecer en Indicadores de Operaciones</span>
            </div>
            <InputSwitch :modelValue="form.incluir_en_kpi" @update:modelValue="form.incluir_en_kpi = $event" />
          </div>
        </div>

        <!-- ── SECCIÓN: Plataforma (solo admin) ── -->
        <div v-if="isAdmin" class="kpi-canal-section">
          <div class="kpi-canal-section-label">
            <i class="pi pi-verified"></i>
            Plataforma
          </div>

          <div class="kpi-canal-platform-row">
            <button
              type="button"
              class="kpi-canal-platform-btn"
              :class="{ 'is-active': !form.sonara }"
              @click="form.sonara = false"
            >
              <img src="/img/vybroo-icon.png" alt="Vybroo" width="24" height="24" class="rounded-full flex-shrink-0" />
              <span class="kpi-canal-platform-name">Vybroo</span>
              <i v-if="!form.sonara" class="pi pi-check" style="font-size:0.7rem;color:#10b981;margin-left:2px"></i>
            </button>
            <button
              type="button"
              class="kpi-canal-platform-btn"
              :class="{ 'is-active': form.sonara }"
              @click="form.sonara = true"
            >
              <svg width="24" height="24" viewBox="0 0 22 22" class="flex-shrink-0">
                <circle cx="11" cy="11" r="10" fill="#ff0000" stroke="#fff" stroke-width="2"/>
                <circle cx="11" cy="11" r="5" fill="#000" stroke="#fff" stroke-width="2"/>
              </svg>
              <span class="kpi-canal-platform-name">Sonara</span>
              <i v-if="form.sonara" class="pi pi-check" style="font-size:0.7rem;color:#10b981;margin-left:2px"></i>
            </button>
          </div>
        </div>

      </div>

      <template #footer>
        <Button label="Cancelar" class="p-button-text" :disabled="guardando" @click="dialogVisible = false" />
        <Button label="Guardar cambios" :loading="guardando" @click="guardar" />
      </template>
    </Dialog>

    <Toast group="canales-metas" position="bottom-right" />
  </div>
</template>

<script>
import KpiSectionSkeleton from '@/components/KpiSectionSkeleton.vue';
import axiosInstance from '@/axiosInstance';
import { useGlobalFilter } from '@/stores/globalFilterStore';

const gf = useGlobalFilter();

export default {
  name: 'IndicadoresOpCanales',

  components: { KpiSectionSkeleton },

  emits: ['update-loading'],

  data() {
    return {
      radios: [],
      loading: false,
      sectionLoadingDelayed: false,
      sectionLoadingTimer: null,
      filtro: '',
      tipoFiltro: 'active',
      asesorSeleccionado: null,
      dialogVisible: false,
      guardando: false,
      canalActivo: null,
      form: {
        type: '',
        obligation_spots: null,
        obligation_ia: 0,
        inicio_operaciones_at: null,
        requiere_llamada: true,
        meta_cuenta: true,
        produccion_100_meta: false,
        sonara: false,
        incluir_en_kpi: false,
      },
    };
  },

  computed: {
    tipoFiltroOptions() {
      return [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Activos' },
        { value: 'demo', label: 'Demos' },
        { value: 'intercambio', label: 'Intercambio' },
        { value: 'cortesia', label: 'Cortesía' },
      ];
    },

    typeOptions() {
      const base = ['demo', 'intercambio', 'cortesia'];
      const fromData = this.radios
        .map((r) => this.normalizarTipo(r.type))
        .filter((t) => t.length > 0);
      return Array.from(new Set(base.concat(fromData))).sort((a, b) => a.localeCompare(b));
    },

    radiosFiltrados() {
      const q = this.filtro.trim().toLowerCase();
      return this.radios.filter((r) => {
        // Filtro global de asesor
        if (this.asesorSeleccionado && Number(r.operator_id) !== Number(this.asesorSeleccionado)) {
          return false;
        }

        const tipo = this.normalizarTipo(r.type);
        const tipoEsActivo = !this.esTipoExcluido(tipo) && this.esCanalOperativo(r);
        const cumpleTipo =
          this.tipoFiltro === 'all' ||
          (this.tipoFiltro === 'active' && tipoEsActivo) ||
          (this.tipoFiltro !== 'all' && this.tipoFiltro !== 'active' && tipo === this.tipoFiltro);

        if (!cumpleTipo) {
          return false;
        }

        if (!q) {
          return true;
        }

        return (
          String(r.name || '').toLowerCase().includes(q) ||
          String(r.client_name || '').toLowerCase().includes(q)
        );
      });
    },

    sectionLoading() {
      return this.sectionLoadingDelayed && this.radios.length > 0;
    },

    isAdmin() {
      try {
        const data = JSON.parse(localStorage.getItem('clientData') || '{}');
        return Array.isArray(data.permissions) && data.permissions.includes('admin');
      } catch { return false; }
    },
  },

  watch: {
    loading(val) {
      if (val) {
        this.sectionLoadingTimer = setTimeout(() => {
          this.sectionLoadingDelayed = true;
        }, 150);
      } else {
        clearTimeout(this.sectionLoadingTimer);
        this.sectionLoadingTimer = null;
        this.sectionLoadingDelayed = false;
      }
    },
  },

  mounted() {
    this.asesorSeleccionado = gf.get('globalAsesorId');
    this._unsubFilter = gf.onChange(() => {
      this.asesorSeleccionado = gf.get('globalAsesorId');
    });
    this.cargar();
  },

  beforeUnmount() {
    clearTimeout(this.sectionLoadingTimer);
    if (this._unsubFilter) this._unsubFilter();
  },

  methods: {
    esCanalOperativo(radio) {
      const disabledVal = radio && radio.disabled;
      const disabled =
        disabledVal === true ||
        disabledVal === 1 ||
        disabledVal === '1' ||
        disabledVal === 't' ||
        disabledVal === 'true';

      const clientId = Number(radio && radio.client_id != null ? radio.client_id : 0);
      return !disabled && clientId > 0;
    },

    normalizarTipo(tipo) {
      const t = String(tipo || '').trim().toLowerCase();
      const map = { 'demos': 'demo', 'cortesía': 'cortesia', 'pruebas': 'demo' };
      return map[t] || t;
    },

    esTipoExcluido(tipo) {
      return ['demo', 'intercambio', 'cortesia'].includes(
        this.normalizarTipo(tipo)
      );
    },

    async cargar() {
      this.loading = true;
      try {
        const res = await axiosInstance.post('/api/canal_metas_list', {
          token: localStorage.getItem('token'),
        });
        if (res.status === 200 && !res.data.error) {
          this.radios = res.data.radios;
          // Reiniciar asesor si el seleccionado ya no existe
          if (this.asesorSeleccionado && this.radios.length) {
            const existe = this.radios.some(r => Number(r.operator_id) === Number(this.asesorSeleccionado));
            if (!existe) {
              this.asesorSeleccionado = null;
              gf.clear('globalAsesorId');
              gf.clear('globalAsesorName');
            }
          }
        } else {
          this.toast('error', 'Error al cargar', res.data.message || 'Error desconocido');
        }
      } catch {
        this.toast('error', 'Error de red', 'No se pudo conectar al servidor');
      } finally {
        this.loading = false;
      }
    },

    abrirEditor(canal) {
      if (!this.isAdmin) return;
      this.canalActivo = canal;
      this.form.type = this.normalizarTipo(canal.type) || '';
      this.form.obligation_spots = canal.obligation_spots ?? null;
      this.form.obligation_ia = canal.obligation_ia ?? 0;
      // Calendar espera Date o null
      this.form.inicio_operaciones_at = canal.inicio_operaciones_at
        ? new Date(canal.inicio_operaciones_at + 'T00:00:00')
        : null;
      this.form.requiere_llamada = canal.requiere_llamada !== false;
      this.form.meta_cuenta = canal.meta_cuenta !== false;
      this.form.produccion_100_meta = canal.produccion_100_meta === true;
      this.form.sonara = canal.sonara === true;
      this.form.incluir_en_kpi = canal.client_id && !canal.id ? true : (canal.incluir_en_kpi === true);
      this.dialogVisible = true;
    },

    async guardar() {
      this.guardando = true;
      try {
        // Convertir Date → 'YYYY-MM-DD' para el backend
        let fechaStr = null;
        if (this.form.inicio_operaciones_at instanceof Date) {
          const d = this.form.inicio_operaciones_at;
          const yy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          fechaStr = `${yy}-${mm}-${dd}`;
        }

        const res = await axiosInstance.post('/api/canal_metas_save', {
          token: localStorage.getItem('token'),
          id: this.canalActivo.id,
          client_id: this.canalActivo.id === null ? this.canalActivo.client_id : null,
          type: this.form.type || null,
          obligation_spots: this.form.obligation_spots,
          obligation_ia: this.form.obligation_ia ?? 0,
          inicio_operaciones_at: fechaStr,
          requiere_llamada: this.form.requiere_llamada,
          meta_cuenta: this.form.meta_cuenta,
          produccion_100_meta: this.form.produccion_100_meta,
          sonara: this.form.sonara,
          incluir_en_kpi: this.form.incluir_en_kpi,
        });

        if (res.status === 200 && !res.data.error) {
          // Actualizar fila en tabla sin recargar todo
          const isClientSinCanal = this.canalActivo.id === null;
          const idx = this.radios.findIndex((r) =>
            isClientSinCanal
              ? (r.id === null && r.client_id === this.canalActivo.client_id)
              : r.id === this.canalActivo.id
          );
          if (idx !== -1) {
            this.radios[idx].type = this.form.type || null;
            this.radios[idx].obligation_spots = this.form.obligation_spots;
            this.radios[idx].obligation_ia = this.form.obligation_ia ?? 0;
            this.radios[idx].inicio_operaciones_at = fechaStr;
            this.radios[idx].requiere_llamada = this.form.requiere_llamada;
            this.radios[idx].meta_cuenta = this.form.meta_cuenta;
            this.radios[idx].produccion_100_meta = this.form.produccion_100_meta;
            this.radios[idx].sonara = this.form.sonara;
          }
          this.dialogVisible = false;
          this.toast('success', 'Guardado', `${this.canalActivo.name} actualizado`);
        } else {
          this.toast('error', 'Error al guardar', res.data.message || 'Error desconocido');
        }
      } catch {
        this.toast('error', 'Error de red', 'No se pudo conectar al servidor');
      } finally {
        this.guardando = false;
      }
    },

    getLogoUrl(data) {
      if (data.mobileimage) return 'https://vybroo.net/images' + data.mobileimage;
      return '';
    },

    logoBackgroundStyle(data) {
      if (!data || !data.color) return { backgroundColor: '#3f3f46' };
      const hex = String(data.color).replace(/^#/, '').toLowerCase();
      if (/^[0-9a-f]{3}$/.test(hex) || /^[0-9a-f]{6}$/.test(hex)) {
        return { backgroundColor: '#' + hex };
      }
      return { backgroundColor: '#3f3f46' };
    },

    toast(severity, summary, detail) {
      this.$toast.add({ severity, summary, detail, group: 'canales-metas', life: 4000 });
    },

    async toggleSonara(radio) {
      try {
        const newVal = !radio.sonara;
        const res = await axiosInstance.post('/api/canal_toggle_sonara', {
          token: localStorage.getItem('token'),
          radio_id: radio.id,
          sonara: newVal,
        });
        if (res.data.success) {
          radio.sonara = newVal;
          this.toast('success', 'Actualizado', `${radio.name} ahora es ${newVal ? 'Sonara' : 'Vybroo'}`);
        }
      } catch {
        this.toast('error', 'Error', 'No se pudo actualizar el tipo de plataforma');
      }
    },
  },
};
</script>

<style>
@import "@/assets/media-module-base.css";

/* Hero diferenciado para sección Indicadores */
.page-ind-canales {
  --media-hero-bg: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  --media-hero-border: rgba(110, 231, 183, 0.4);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(20, 30, 25, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  --media-hero-border-dark: rgba(52, 211, 153, 0.2);
}

.ind-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.ind-toolbar-search {
  position: relative;
  flex: 0 0 320px;
  min-width: 260px;
  max-width: 360px;
  flex-shrink: 0;
}

.ind-toolbar-types {
  flex: 1 1 0;
  min-width: 420px;
  display: flex;
  justify-content: flex-end;
}

.ind-filter-input {
  width: 100%;
  height: 42px;
  border-radius: 10px;
}

.ind-filter-input-with-icon.p-inputtext {
  padding-left: 2rem;
}

.ind-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.76rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}

.ind-filter-input-with-icon {
  padding-left: 2rem;
}

.ind-filter-input-with-icon::placeholder {
  color: #94a3b8;
}

.ind-type-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-height: 42px;
  padding-top: 2px;
  justify-content: flex-end;
  width: 100%;
}

.ind-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  padding: 4px 9px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #475569;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  user-select: none;
  transition: border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.ind-type-chip input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.ind-type-chip::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.85);
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.ind-type-chip:hover {
  transform: translateY(-1px);
}

.ind-type-chip.is-active {
  box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.4);
}

.ind-type-chip:nth-of-type(1).is-active {
  border-color: rgba(59, 130, 246, 0.6);
  background: rgba(219, 234, 254, 0.9);
  color: #1d4ed8;
}

.ind-type-chip:nth-of-type(1).is-active::before {
  background: #3b82f6;
}

.ind-type-chip:nth-of-type(2).is-active {
  border-color: rgba(34, 197, 94, 0.6);
  background: rgba(220, 252, 231, 0.95);
  color: #15803d;
}

.ind-type-chip:nth-of-type(2).is-active::before {
  background: #22c55e;
}

.ind-type-chip:nth-of-type(3).is-active {
  border-color: rgba(107, 114, 128, 0.6);
  background: rgba(243, 244, 246, 0.95);
  color: #4b5563;
}

.ind-type-chip:nth-of-type(3).is-active::before {
  background: #6b7280;
}

.ind-type-chip:nth-of-type(4).is-active {
  border-color: rgba(234, 179, 8, 0.65);
  background: rgba(254, 249, 195, 0.95);
  color: #a16207;
}

.ind-type-chip:nth-of-type(4).is-active::before {
  background: #eab308;
}

.ind-type-chip:nth-of-type(5).is-active {
  border-color: rgba(168, 85, 247, 0.62);
  background: rgba(243, 232, 255, 0.95);
  color: #7e22ce;
}

.ind-type-chip:nth-of-type(5).is-active::before {
  background: #a855f7;
}

.ind-type-chip:nth-of-type(6).is-active {
  border-color: rgba(236, 72, 153, 0.58);
  background: rgba(252, 231, 243, 0.95);
  color: #be185d;
}

.ind-type-chip:nth-of-type(6).is-active::before {
  background: #ec4899;
}

.dark .ind-type-chip {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #d4d4d8;
}

.dark .ind-type-chip::before {
  background: rgba(113, 113, 122, 0.75);
}

.dark .ind-type-chip:hover {
  border-color: rgba(96, 165, 250, 0.55);
  background: rgba(30, 41, 59, 0.85);
  color: #bfdbfe;
}

.dark .ind-type-chip:nth-of-type(1).is-active {
  border-color: rgba(96, 165, 250, 0.75);
  background: rgba(30, 58, 138, 0.3);
  color: #dbeafe;
}

.dark .ind-type-chip:nth-of-type(1).is-active::before {
  background: #60a5fa;
}

.dark .ind-type-chip:nth-of-type(2).is-active {
  border-color: rgba(74, 222, 128, 0.75);
  background: rgba(20, 83, 45, 0.28);
  color: #dcfce7;
}

.dark .ind-type-chip:nth-of-type(2).is-active::before {
  background: #4ade80;
}

.dark .ind-type-chip:nth-of-type(3).is-active {
  border-color: rgba(156, 163, 175, 0.72);
  background: rgba(63, 63, 70, 0.3);
  color: #e4e4e7;
}

.dark .ind-type-chip:nth-of-type(3).is-active::before {
  background: #9ca3af;
}

.dark .ind-type-chip:nth-of-type(4).is-active {
  border-color: rgba(250, 204, 21, 0.75);
  background: rgba(113, 63, 18, 0.32);
  color: #fef3c7;
}

.dark .ind-type-chip:nth-of-type(4).is-active::before {
  background: #facc15;
}

.dark .ind-type-chip:nth-of-type(5).is-active {
  border-color: rgba(192, 132, 252, 0.75);
  background: rgba(88, 28, 135, 0.3);
  color: #f3e8ff;
}

.dark .ind-type-chip:nth-of-type(5).is-active::before {
  background: #c084fc;
}

.dark .ind-type-chip:nth-of-type(6).is-active {
  border-color: rgba(244, 114, 182, 0.72);
  background: rgba(131, 24, 67, 0.3);
  color: #fce7f3;
}

.dark .ind-type-chip:nth-of-type(6).is-active::before {
  background: #f472b6;
}

.ind-form-select {
  width: 100%;
  height: 42px;
  border: 1px solid #d4d4d8;
  border-radius: 10px;
  padding: 0 12px;
  background: #fff;
  color: #111827;
}

.dark .ind-form-select {
  border-color: #3f3f46;
  background: #18181b;
  color: #f4f4f5;
}

.ind-toolbar-count {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4px 0 8px;
  line-height: 1;
  opacity: 0.72;
}

@media (max-width: 1024px) {
  .ind-toolbar-search {
    width: 100%;
    flex: 1 1 100%;
    max-width: none;
  }

  .ind-toolbar-types {
    min-width: 0;
    width: 100%;
    justify-content: flex-start;
  }

  .ind-toolbar-count {
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .ind-toolbar-search {
    width: 100%;
  }
}

/* ── Shimmer overlay en secciones durante recarga ── */
.kpi-section--loading {
  position: relative;
}

.kpi-section--loading::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: kpi-loading-shimmer 1.8s ease-in-out infinite;
  z-index: 2;
  pointer-events: none;
}

.dark .kpi-section--loading::before {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.08) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
}

@keyframes kpi-loading-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Loading dot animado en títulos ── */
.kpi-loading-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #6366f1;
  animation: kpi-loading-dot-pulse 1.4s ease-in-out infinite;
  vertical-align: middle;
  position: relative;
  top: -1px;
  margin-left: 6px;
}

.dark .kpi-loading-dot {
  background: #818cf8;
}

@keyframes kpi-loading-dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.6); }
}

/* ── Skeleton page (carga en frío) ── */
.kpi-skel-page {
  animation: kpi-skel-fade-in 0.4s ease-out;
}

@keyframes kpi-skel-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.kpi-skel-loading-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.dark .kpi-skel-loading-label {
  color: #52525b;
}

/* Fade-in suave del contenido real al terminar skeletons */
.kpi-page-content {
  animation: kpi-content-fade-in 0.35s ease-out;
}

@keyframes kpi-content-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Secciones estilo KPI ── */
.kpi-section {
  position: relative;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 12px;
  padding: 16px 18px;
}

.dark .kpi-section {
  background: rgba(24, 24, 27, 0.5);
  border-color: rgba(82, 82, 91, 0.4);
}

.kpi-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #1e293b;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dark .kpi-section-title {
  color: #d4d4d8;
}

/* ── Dialog: Editor de canal ── */
.kpi-canal-section {
  background: rgba(0,0,0,0.02);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dark .kpi-canal-section {
  background: rgba(255,255,255,0.02);
  border-color: rgba(255,255,255,0.06);
}
.kpi-canal-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}
.dark .kpi-canal-section-label { color: #9ca3af; border-bottom-color: rgba(255,255,255,0.05); }
.kpi-canal-field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
}
.dark .kpi-canal-field-label { color: #d1d5db; }
.kpi-canal-select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 7px;
  font-size: 0.85rem;
  background: #fff;
  color: #1f2937;
  outline: none;
  transition: border-color 0.15s;
}
.kpi-canal-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.15); }
.dark .kpi-canal-select { background: rgba(0,0,0,0.25); border-color: rgba(255,255,255,0.1); color: #e5e7eb; }
.kpi-canal-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.12s;
}
.kpi-canal-toggle-row:hover { background: rgba(59,130,246,0.04); }
.kpi-canal-toggle-info { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.kpi-canal-toggle-title { font-size: 0.82rem; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 5px; }
.dark .kpi-canal-toggle-title { color: #e5e7eb; }
.kpi-canal-toggle-desc { font-size: 0.72rem; color: #9ca3af; line-height: 1.3; }

/* ── Platform selector ── */
.kpi-canal-platform-row {
  display: flex;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.1);
}
.dark .kpi-canal-platform-row { border-color: rgba(255,255,255,0.1); }
.kpi-canal-platform-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  font-size: 0.82rem;
  font-weight: 500;
  color: #6b7280;
  outline: none;
}
.kpi-canal-platform-btn:first-child { border-right: 1px solid rgba(0,0,0,0.1); }
.dark .kpi-canal-platform-btn:first-child { border-right-color: rgba(255,255,255,0.1); }
.kpi-canal-platform-btn:hover { background: rgba(0,0,0,0.03); }
.kpi-canal-platform-btn.is-active {
  background: rgba(59,130,246,0.08);
  color: #1e40af;
  font-weight: 600;
}
.dark .kpi-canal-platform-btn.is-active {
  background: rgba(59,130,246,0.12);
  color: #93c5fd;
}
.kpi-canal-platform-badge {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(0,0,0,0.06);
  color: #9ca3af;
}
.dark .kpi-canal-platform-badge { background: rgba(255,255,255,0.06); }
.kpi-canal-platform-btn.is-active .kpi-canal-platform-badge {
  background: rgba(59,130,246,0.12);
  color: #3b82f6;
}
</style>
